/**
 * BuddyBubble — 像素对话气泡（工单 5）
 * 平滑淡入淡出 · 30ms/字打字机 · anchor 定位 + 尾巴指向伴侣
 * 自适应边界：超出视口时翻转/钳制，不得溢出屏幕 · 文案内 [CTA] 可点击直达
 */
import {
  useCallback,
  useLayoutEffect,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { TYPE_MS_PER_CHAR, type BuddyLine } from "@/lib/buddyLines";
import { audioEngine } from "@/lib/audioEngine";
import {
  BUDDY_IDENTITY_META,
  getBuddyIdentity,
  resolveIdentityLine,
  subscribeBuddyIdentity,
} from "@/lib/buddyVoice";

/** 打字机滴滴声节流：每 N 字一滴（30ms/字 → 60ms 一滴，配 24ms 防叠爆兜底） */
const BLIP_EVERY_N_CHARS = 2;

export interface BuddyBubbleProps {
  /** 当前气泡文案（null → 淡出收起）；类型见 buddyLines.BuddyLine */
  line: BuddyLine | null;
  /** 伴侣头顶锚点（相对定位容器的坐标），尾巴指向此处 */
  anchor: { x: number; y: number };
  /** 点击气泡内 CTA 后回调 */
  onCta?: () => void;
  className?: string;
}

/** 气泡与锚点的间距 */
const GAP = 12;
/** 视口安全边距 */
const VIEW_PAD = 8;
/** 淡入/淡出时长 */
const FADE_MS = 220;
/** 换文案时的交叉淡出时长（略短于 FADE_MS，旧文淡至近透明再换） */
const SWAP_DIP_MS = 160;
/** 尾巴半宽（外轮廓 / 内芯） */
const TAIL_W = 8;
const TAIL_W_IN = 5;

type Layout = {
  left: number;
  top: number;
  /** 尾巴尖在气泡内的水平位置 */
  tailX: number;
  /** "bottom"=气泡在锚点上方尾巴朝下；"top"=翻转到锚点下方尾巴朝上 */
  side: "bottom" | "top";
};

/**
 * 文案渲染：打字机吐字 + 文案内 [CTA] 链接化。
 * interactive=false 时输出完整纯文本（供尺寸占位层使用，保证高度稳定）。
 */
function renderContent(
  line: BuddyLine,
  typed: string,
  opts: { interactive: boolean; typing: boolean; onCta?: () => void },
) {
  const cursor = opts.typing ? (
    <span className="animate-px-blink text-[#00f0ff]">▊</span>
  ) : null;
  const token = line.cta ? `[${line.cta.label}]` : null;
  const idx = token ? line.text.indexOf(token) : -1;
  if (!line.cta || !token || idx < 0) {
    return (
      <>
        {typed}
        {cursor}
      </>
    );
  }
  const before = typed.slice(0, Math.min(typed.length, idx));
  const typedToken =
    typed.length > idx ? typed.slice(idx, idx + token.length) : "";
  const after =
    typed.length > idx + token.length ? typed.slice(idx + token.length) : "";
  const tokenDone = typedToken.length >= token.length;
  return (
    <>
      {before}
      {tokenDone && opts.interactive ? (
        <a
          href={line.cta.href}
          target="_blank"
          rel="noopener noreferrer"
          tabIndex={-1}
          className="pointer-events-auto text-[#38bdf8] underline decoration-dotted underline-offset-2 transition-colors hover:text-[#fbbf24]"
          onClick={() => opts.onCta?.()}
        >
          {token}
        </a>
      ) : (
        typedToken
      )}
      {after}
      {cursor}
    </>
  );
}

export function BuddyBubble({
  line,
  anchor,
  onCta,
  className = "",
}: BuddyBubbleProps) {
  const [shown, setShown] = useState<BuddyLine | null>(null);
  const [typed, setTyped] = useState("");
  const [visible, setVisible] = useState(false);
  const [layout, setLayout] = useState<Layout | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  /** 出场角色身份（工单双身份）：iCLOSER 换徽记/口吻/琥珀描边，KERNELHUB 保持信 XIN */
  const identity = useSyncExternalStore(
    subscribeBuddyIdentity,
    getBuddyIdentity,
    getBuddyIdentity,
  );
  const idMeta = BUDDY_IDENTITY_META[identity];
  /** 身份呈现层：首次原文映射 iCloser 自我介绍（轮换台词本身已按身份出词） */
  const view = shown ? resolveIdentityLine(shown, identity) : null;
  /** 气泡 chrome：kernelhub=冰青（现状不动）/ icloser=琥珀金 */
  const chrome =
    identity === "icloser"
      ? { border: "#fbbf24", glow: "rgba(251,191,36,0.35)" }
      : { border: "#38bdf8", glow: "rgba(56,189,248,0.35)" };

  const ax = anchor.x;
  const ay = anchor.y;

  // ── 自适应边界：优先锚点上方（尾巴朝下）→ 顶部钳制 → 翻转到锚点下方 → 底部钳制 ──
  const measure = useCallback(() => {
    const el = wrapRef.current;
    const host = el?.parentElement;
    if (!el || !host) return;
    const { width, height } = el.getBoundingClientRect();
    const hostRect = host.getBoundingClientRect();
    const axV = hostRect.left + ax;
    const ayV = hostRect.top + ay;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let side: Layout["side"] = "bottom";
    let topV = ayV - height - GAP;
    if (topV < VIEW_PAD) {
      if (VIEW_PAD + height + GAP <= ayV) {
        // 顶部放不下但贴锚点仍有余：钳制在视口顶部
        topV = VIEW_PAD;
      } else {
        // 翻转到锚点下方（尾巴朝上）
        side = "top";
        topV = ayV + GAP;
        if (topV + height > vh - VIEW_PAD) {
          topV = Math.max(VIEW_PAD, vh - height - VIEW_PAD);
        }
      }
    }

    const leftV = Math.max(
      VIEW_PAD,
      Math.min(axV - width / 2, vw - width - VIEW_PAD),
    );
    const tailX = Math.max(TAIL_W + 2, Math.min(axV - leftV, width - TAIL_W - 2));

    setLayout({
      left: leftV - hostRect.left,
      top: topV - hostRect.top,
      tailX,
      side,
    });
  }, [ax, ay]);

  // ── line 变化：淡入挂载 / 短交叉淡出换文 / 淡出卸载 ──
  useEffect(() => {
    if (!line) {
      setVisible(false);
      const t = window.setTimeout(() => setShown(null), FADE_MS);
      return () => window.clearTimeout(t);
    }
    let cancelled = false;
    let raf = 0;
    let t = 0;
    const mount = () => {
      if (cancelled) return;
      setShown(line);
      raf = requestAnimationFrame(() => {
        if (!cancelled) setVisible(true);
      });
    };
    // 对象换新即视为换文（custom 行 id 相同但 text 不同，按引用区分）
    if (shown && shown !== line) {
      setVisible(false);
      t = window.setTimeout(mount, SWAP_DIP_MS);
    } else {
      mount();
    }
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      if (t) window.clearTimeout(t);
    };
    // shown 仅用于判断是否换文案，不进依赖避免重复触发
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [line]);

  // ── 打字机：30ms/字（layout effect 避免换文首帧残留旧字）+ 像素滴滴声 ──
  // 身份切换（READY 选角）后按呈现层文案重打：kernelhub=原文 / icloser=iCloser 口吻
  useLayoutEffect(() => {
    if (!shown) return;
    const text = resolveIdentityLine(shown, identity).text;
    setTyped("");
    let i = 0;
    const id = window.setInterval(() => {
      i += 1;
      setTyped(text.slice(0, i));
      if (i >= text.length) {
        // 说完收尾一记高音滴滴
        audioEngine.play("blip-hi");
        window.clearInterval(id);
      } else if (i % BLIP_EVERY_N_CHARS === 0) {
        // 逐字吐字节流滴（中音为主，偶发低音，像素 NPC 嘟嘟嘟）
        audioEngine.play(i % 6 === 0 ? "blip-lo" : "blip");
      }
    }, TYPE_MS_PER_CHAR);
    return () => window.clearInterval(id);
  }, [shown, identity]);

  // ── 尺寸变化（换文/字库加载）时重新钳制 ──
  useLayoutEffect(() => {
    measure();
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [measure, shown]);

  if (!shown || !view) return null;

  const typing = typed.length < view.text.length;

  return (
    <div
      ref={wrapRef}
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className={`pointer-events-none z-[70] w-[min(320px,calc(100vw-24px))] border-[3px] bg-[#151a28] p-3 transition-opacity duration-200 ${className}`}
      data-identity={identity}
      style={{
        position: "absolute",
        left: layout ? layout.left : 0,
        top: layout ? layout.top : 0,
        opacity: visible && layout ? 1 : 0,
        borderColor: chrome.border,
        // 8-bit 外凸倒角 + 身份色霓虹光晕（批准色板：冰青 #38bdf8 / 琥珀金 #fbbf24 / 钛白 #f1f5f9）
        boxShadow: `inset 2px 2px 0 0 rgba(241, 245, 249, 0.85), inset -2px -2px 0 0 #404040, 4px 4px 0 0 #101010, 0 0 12px ${chrome.glow}`,
      }}
    >
      {/* 完整文案：读屏一次播报；opacity-0 占位层锁定高度，打字机换行不跳动 */}
      <span className="sr-only">{view.text}</span>

      {/* 尾巴：外轮廓 + 内芯，尖端指向伴侣锚点 */}
      {layout ? (
        layout.side === "bottom" ? (
          <>
            <div
              aria-hidden
              style={{
                position: "absolute",
                left: layout.tailX - TAIL_W,
                bottom: -TAIL_W + 1,
                width: 0,
                height: 0,
                borderLeft: `${TAIL_W}px solid transparent`,
                borderRight: `${TAIL_W}px solid transparent`,
                borderTop: `${TAIL_W}px solid ${chrome.border}`,
              }}
            />
            <div
              aria-hidden
              style={{
                position: "absolute",
                left: layout.tailX - TAIL_W_IN,
                bottom: -TAIL_W_IN + 1,
                width: 0,
                height: 0,
                borderLeft: `${TAIL_W_IN}px solid transparent`,
                borderRight: `${TAIL_W_IN}px solid transparent`,
                borderTop: `${TAIL_W_IN}px solid #151a28`,
              }}
            />
          </>
        ) : (
          <>
            <div
              aria-hidden
              style={{
                position: "absolute",
                left: layout.tailX - TAIL_W,
                top: -TAIL_W + 1,
                width: 0,
                height: 0,
                borderLeft: `${TAIL_W}px solid transparent`,
                borderRight: `${TAIL_W}px solid transparent`,
                borderBottom: `${TAIL_W}px solid ${chrome.border}`,
              }}
            />
            <div
              aria-hidden
              style={{
                position: "absolute",
                left: layout.tailX - TAIL_W_IN,
                top: -TAIL_W_IN + 1,
                width: 0,
                height: 0,
                borderLeft: `${TAIL_W_IN}px solid transparent`,
                borderRight: `${TAIL_W_IN}px solid transparent`,
                borderBottom: `${TAIL_W_IN}px solid #151a28`,
              }}
            />
          </>
        )
      ) : null}

      {/* 气泡头徽记：XIN // （kernelhub 保持）/ iCLOSER // （琥珀金） */}
      <div
        className="mb-1 font-pixel text-[7px] tracking-wider"
        style={{ color: chrome.border }}
      >
        {idMeta.badge} // {view.action}
      </div>

      <div className="relative">
        {/* 尺寸占位层：完整文案（含光标位），不可见仅撑高度 */}
        <p
          aria-hidden
          className="pointer-events-none opacity-0 font-zpix text-[12px] leading-6 text-[#f1f5f9]"
        >
          {renderContent(view, view.text, {
            interactive: false,
            typing: true,
          })}
        </p>
        {/* 视觉层：打字机吐字 + 可点 CTA */}
        <p
          aria-hidden
          className="absolute inset-0 overflow-hidden font-zpix text-[12px] leading-6 text-[#f1f5f9]"
        >
          {renderContent(view, typed, { interactive: true, typing, onCta })}
        </p>
      </div>
    </div>
  );
}

export default BuddyBubble;
